import React from 'react';

import dayjs from 'utils/extendedDayJS';
import { EventType, Session } from 'types/event.types';
import { GuestStatus } from 'types/guest.types';
import { Separator } from '@epoch-ui/components/Separator';
import { SessionCard, SessionCardTitleAndDate } from 'components/cards/SessionCard';
import { cn } from '@epoch-ui/lib/utils';
import { RsvpSticker } from 'components/cards/components/RsvpSticker/RsvpSticker';

import { useGuestSideSheetContext } from '../guestSideSheetContext';

const registrationStatuses = [
  GuestStatus.CONFIRMED,
  GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY,
  GuestStatus.WAITLISTED,
  GuestStatus.INTERESTED,
  GuestStatus.CANCELLED
];

export const GuestSideSheetSessionsSection = (): React.ReactNode => {
  const {
    eventOrSessionId,
    guestData: { event, sessionStatuses }
  } = useGuestSideSheetContext();
  const sessions = event.sessions || [];
  const hasSessions = event.eventType === EventType.MULTI_SESSIONS && sessions.length > 0;
  if (!hasSessions) return null;

  const eventTimezone = event.timezoneCode || 'UTC';
  const displayedTimezone = dayjs.tz.guess() || eventTimezone;

  const sessionStatusMap = sessionStatuses.reduce((acc, { id, status }) => {
    acc[id] = status;
    return acc;
  }, {} as { [id: number]: GuestStatus });

  const numRegisteredSessions = sessionStatuses.filter(({ status }) =>
    registrationStatuses.includes(status)
  );
  return (
    <>
      <Separator />
      <div className="tw-flex tw-flex-col tw-gap-4">
        <span className="tw-flex tw-items-center tw-text-base tw-font-medium">
          Sessions{' '}
          {numRegisteredSessions.length > 0
            ? `(${numRegisteredSessions.length} of ${sessions.length})`
            : ''}
        </span>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-2">
        {sessions.map((session: Session) => (
          <SessionCard
            className={cn(
              'tw-cursor-default tw-rounded-[12px]',
              session.id === eventOrSessionId ? 'tw-border-rsvp-blue' : 'tw-opacity-50'
            )}
            key={session.id}
            session={session}
            eventTimezone={eventTimezone}
            displayedTimezone={displayedTimezone}
          >
            <SessionCardTitleAndDate />
            {registrationStatuses.includes(sessionStatusMap[session.id]) && (
              <div className="tw-flex tw-items-center tw-justify-between">
                <RsvpSticker
                  className="tw-rounded-xl tw-p-2"
                  rsvpStatus={sessionStatusMap[session.id]}
                />
              </div>
            )}
          </SessionCard>
        ))}
      </div>
    </>
  );
};
